import React, { useEffect, useState } from 'react'
import useSWR from 'swr'
import fetcher from '@lib/fetcher'
import { IMAGE_IMPORT_EVENT } from './image-import'

const parse = (value, fallback) => (typeof value === 'string' ? JSON.parse(value) : (value || fallback))

const formatDate = (value) => (value
  ? new Date(value).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  : 'No upcoming dates')

const EventRow = ({ event, action }) => (
  <li className='flex items-center gap-3 py-2'>
    <div className='w-16 aspect-[4/3] shrink-0 rounded overflow-hidden bg-gray-200 dark:bg-gray-700'>
      {event.image_url && <img src={event.image_url} alt='' className='w-full h-full object-cover' />}
    </div>
    <div className='flex-1 min-w-0'>
      <p className='text-sm font-medium text-gray-900 dark:text-white truncate'>{event.name}</p>
      <p className='text-xs text-gray-500 dark:text-gray-400'>Next: {formatDate(event.next_start)}</p>
    </div>
    {action}
  </li>
)

// Asks whether an imageless group should take the first added event's image.
const AdoptImagePrompt = ({ event, onAccept, onDecline }) => (
  <div role='alert' className='rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 p-4 flex items-center gap-4'>
    {event.image_url && <img src={event.image_url} alt='' className='w-20 aspect-[4/3] object-cover rounded' />}
    <div className='flex-1 text-sm text-blue-900 dark:text-blue-100'>
      This group has no image. Use the image from <strong>{event.name}</strong>?
    </div>
    <div className='flex gap-2 shrink-0'>
      <button type='button' onClick={onAccept} className='px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white'>
        Use this image
      </button>
      <button type='button' onClick={onDecline} className='px-3 py-1.5 rounded-md text-sm font-medium bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100'>
        No thanks
      </button>
    </div>
  </div>
)

// The group form's Sessions panel: current members, suggested ungrouped
// events whose name contains the group name, and a search. Writes the
// chosen ids to event_group[event_ids][] for the form to save. Stays quiet
// about images while one is picked in the form's image import.
const EventGroupMembers = ({ initialMembers, groupName, groupHasImage, imageImported, eventsUrl }) => {
  const [members, setMembers] = useState(parse(initialMembers, []))
  const [query, setQuery] = useState('')
  const [prompt, setPrompt] = useState(null)
  const [adopted, setAdopted] = useState(null)
  const [imported, setImported] = useState(String(imageImported) === 'true')
  const hasImage = String(groupHasImage) === 'true' || imported

  useEffect(() => {
    const onImport = (e) => {
      setImported(Boolean(e.detail))
      if (e.detail) {
        setPrompt(null)
        setAdopted(null)
      }
    }
    window.addEventListener(IMAGE_IMPORT_EVENT, onImport)
    return () => window.removeEventListener(IMAGE_IMPORT_EVENT, onImport)
  }, [])

  const searchUrl = (term) => `${eventsUrl}?ungrouped=1&search=${encodeURIComponent(term)}`
  const { data: suggestions } = useSWR(groupName ? searchUrl(groupName) : null, fetcher)
  const { data: results } = useSWR(query.length >= 2 ? searchUrl(query) : null, fetcher)

  const memberIds = new Set(members.map(m => m.id))
  const notMember = (event) => !memberIds.has(event.id)

  const add = (event) => {
    setMembers([...members, event])
    if (!hasImage && !adopted && !prompt && event.image_url) setPrompt(event)
  }

  const remove = (event) => {
    setMembers(members.filter(m => m.id !== event.id))
    if (prompt?.id === event.id) setPrompt(null)
    if (adopted?.id === event.id) setAdopted(null)
  }

  const addButton = (event) => (
    <button type='button' onClick={() => add(event)} className='shrink-0 px-3 py-1 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white'>
      Add
    </button>
  )

  return (
    <div className='space-y-6'>
      {/* An empty value lets removing every member clear the group */}
      <input type='hidden' name='event_group[event_ids][]' value='' />
      {members.map(m => (
        <input key={m.id} type='hidden' name='event_group[event_ids][]' value={m.id} />
      ))}
      {adopted && <input type='hidden' name='event_group[adopt_image_from_event_id]' value={adopted.id} />}

      {prompt && (
        <AdoptImagePrompt
          event={prompt}
          onAccept={() => { setAdopted(prompt); setPrompt(null) }}
          onDecline={() => setPrompt(null)}
        />
      )}

      {adopted && (
        <p className='text-sm text-green-700 dark:text-green-300'>
          The image from <strong>{adopted.name}</strong> will be used for this group when you save, unless you upload one.
        </p>
      )}

      <div>
        <h3 className='text-sm font-medium text-gray-700 dark:text-gray-300'>In this group ({members.length})</h3>
        {members.length === 0 ? (
          <p className='mt-2 text-sm text-gray-500 dark:text-gray-400'>No events yet. Add some from the suggestions or search below.</p>
        ) : (
          <ul className='divide-y divide-gray-200 dark:divide-gray-700'>
            {members.map(event => (
              <EventRow
                key={event.id}
                event={event}
                action={
                  <button type='button' onClick={() => remove(event)} className='shrink-0 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'>
                    Remove
                  </button>
                }
              />
            ))}
          </ul>
        )}
      </div>

      {suggestions?.filter(notMember).length > 0 && (
        <div>
          <h3 className='text-sm font-medium text-gray-700 dark:text-gray-300'>Suggested</h3>
          <p className='text-xs text-gray-500 dark:text-gray-400'>Upcoming events not in a group whose name contains “{groupName}”</p>
          <ul className='divide-y divide-gray-200 dark:divide-gray-700'>
            {suggestions.filter(notMember).map(event => (
              <EventRow key={event.id} event={event} action={addButton(event)} />
            ))}
          </ul>
        </div>
      )}

      <div>
        <label htmlFor='event-group-member-search' className='text-sm font-medium text-gray-700 dark:text-gray-300'>Find events</label>
        <input
          id='event-group-member-search'
          type='search'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Search upcoming events not in a group…'
          className='mt-1 block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm'
        />
        {results && (
          <ul className='divide-y divide-gray-200 dark:divide-gray-700'>
            {results.filter(notMember).map(event => (
              <EventRow key={event.id} event={event} action={addButton(event)} />
            ))}
            {results.filter(notMember).length === 0 && (
              <li className='py-2 text-sm text-gray-500 dark:text-gray-400'>No matching events.</li>
            )}
          </ul>
        )}
      </div>
    </div>
  )
}

export default EventGroupMembers
